import { TreeIndex } from "./treeIndex";

/** The pure half of reveal: which items must expand. Scrolling lives in `useTreeReveal`. */
export type RevealPlan = {
  /** Reference-equal to the input when nothing changed. */
  expandedItems: string[];
  /** Outermost first; empty for a top-level target. */
  ancestors: string[];
  didExpand: boolean;
};

export type PlanRevealOptions = {
  /** Also expand the target. Only meaningful for a folder. */
  expandTarget?: boolean;
};

/** `null` when the target is unknown: the caller should stay queued, not treat it as a no-op. */
export function planReveal<D>(
  index: TreeIndex<D>,
  targetId: string,
  expandedItems: readonly string[],
  options: PlanRevealOptions = {},
): RevealPlan | null {
  if (!index.has(targetId)) return null;

  const ancestors = index.getPath(targetId);
  const toExpand =
    options.expandTarget === true ? [...ancestors, targetId] : ancestors;

  const alreadyExpanded = new Set(expandedItems);
  const missing = toExpand.filter((id) => !alreadyExpanded.has(id));

  if (missing.length === 0) {
    return {
      // Reference-equal so callers can skip the state write.
      expandedItems: expandedItems as string[],
      ancestors,
      didExpand: false,
    };
  }

  return {
    expandedItems: [...expandedItems, ...missing],
    ancestors,
    didExpand: true,
  };
}
